import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Card, Row, Col, InputGroup, ListGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAvailableStores } from '../services/priceService';

const StoreSelector = ({ onCompareClick }) => {
  const [stores, setStores] = useState([]);
  const [selectedStore1, setSelectedStore1] = useState('');
  const [selectedStore2, setSelectedStore2] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm1, setSearchTerm1] = useState('');
  const [searchTerm2, setSearchTerm2] = useState('');
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const searchRef1 = useRef(null);
  const searchRef2 = useRef(null);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const storesData = await getAvailableStores();
        setStores(storesData);
        
        // Set default selections if stores are available
        if (storesData.length >= 2) {
          setSelectedStore1(storesData[0].id);
          setSelectedStore2(storesData[1].id);
        } else if (storesData.length === 0) {
          setError('No stores available. Please try again later or check your connection.');
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load stores. Please try again later.');
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  // Handle clicks outside the search dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef1.current && !searchRef1.current.contains(event.target)) {
        setShowDropdown1(false);
      }
      if (searchRef2.current && !searchRef2.current.contains(event.target)) {
        setShowDropdown2(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCompareClick = () => {
    if (selectedStore1 && selectedStore2) {
      onCompareClick(selectedStore1, selectedStore2);
    }
  };

  const handleStoreSelect = (storeId, storeName, isFirstStore) => {
    if (isFirstStore) {
      setSelectedStore1(storeId);
      setSearchTerm1(storeName);
      setShowDropdown1(false);
    } else {
      setSelectedStore2(storeId);
      setSearchTerm2(storeName);
      setShowDropdown2(false);
    }
  };

  // Function to highlight matching text in search results
  const highlightMatch = (text, query) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? <span key={index} style={{ backgroundColor: 'var(--apple-accent-soft)', color: 'var(--apple-accent)', borderRadius: 3 }}>{part}</span> : part
    );
  };

  if (loading) {
    return <div className="text-center my-4">Loading stores...</div>;
  }

  if (error) {
    return <div className="text-center my-4 text-danger">{error}</div>;
  }

  return (
    <Card className="mb-4">
      <Card.Header>
        <h4 className="mb-0" style={{ fontSize: 'var(--font-title)', fontWeight: 600, letterSpacing: '-0.3px' }}>Select Stores to Compare</h4>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={5}>
            <Form.Group className="mb-3" ref={searchRef1}>
              <Form.Label>First Store</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  ⌕
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search for a store..."
                  value={searchTerm1}
                  onChange={(e) => {
                    setSearchTerm1(e.target.value);
                    setShowDropdown1(true);
                  }}
                  onFocus={() => setShowDropdown1(true)}
                />
                {searchTerm1 && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => {
                      setSearchTerm1('');
                      setShowDropdown1(true);
                    }}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
              
              {showDropdown1 && (
                <div className="position-relative">
                  <ListGroup 
                    className="position-absolute w-100 shadow-sm" 
                    style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                  >
                    {stores
                      .filter(store => 
                        !searchTerm1 || 
                        store.name.toLowerCase().includes(searchTerm1.toLowerCase())
                      )
                      .map((store) => (
                        <ListGroup.Item 
                          key={`store1-${store.chain}-${store.id}`}
                          action
                          active={selectedStore1 === store.id}
                          onClick={() => handleStoreSelect(store.id, store.name, true)}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="d-flex align-items-center">
                            <div>{highlightMatch(store.name, searchTerm1)}</div>
                            <Link 
                              to={`/store/${store.id}?chain=${store.chain || ''}`}
                              className="ms-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Badge bg="info" pill>
                                Details
                              </Badge>
                            </Link>
                          </div>
                          {selectedStore1 === store.id && (
                            <span className="text-primary">✓</span>
                          )}
                        </ListGroup.Item>
                      ))
                    }
                    {stores.filter(store => 
                      !searchTerm1 || 
                      store.name.toLowerCase().includes(searchTerm1.toLowerCase())
                    ).length === 0 && (
                      <ListGroup.Item className="text-muted">
                        No stores found
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </div>
              )}
              
              {/* Hidden input to store the selected value */}
              <input type="hidden" value={selectedStore1} />
            </Form.Group>
          </Col>
          
          <Col md={2} className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <span className="h4">VS</span>
            </div>
          </Col>
          
          <Col md={5}>
            <Form.Group className="mb-3" ref={searchRef2}>
              <Form.Label>Second Store</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  ⌕
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search for a store..."
                  value={searchTerm2}
                  onChange={(e) => {
                    setSearchTerm2(e.target.value);
                    setShowDropdown2(true);
                  }}
                  onFocus={() => setShowDropdown2(true)}
                />
                {searchTerm2 && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => {
                      setSearchTerm2('');
                      setShowDropdown2(true);
                    }}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
              
              {showDropdown2 && (
                <div className="position-relative">
                  <ListGroup 
                    className="position-absolute w-100 shadow-sm" 
                    style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                  >
                    {stores
                      .filter(store => 
                        !searchTerm2 || 
                        store.name.toLowerCase().includes(searchTerm2.toLowerCase())
                      )
                      .map((store) => (
                        <ListGroup.Item 
                          key={`store2-${store.chain}-${store.id}`}
                          action
                          active={selectedStore2 === store.id}
                          onClick={() => handleStoreSelect(store.id, store.name, false)}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="d-flex align-items-center">
                            <div>{highlightMatch(store.name, searchTerm2)}</div>
                            <Link 
                              to={`/store/${store.id}?chain=${store.chain || ''}`}
                              className="ms-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Badge bg="info" pill>
                                Details
                              </Badge>
                            </Link>
                          </div>
                          {selectedStore2 === store.id && (
                            <span className="text-primary">✓</span>
                          )}
                        </ListGroup.Item>
                      ))
                    }
                    {stores.filter(store => 
                      !searchTerm2 || 
                      store.name.toLowerCase().includes(searchTerm2.toLowerCase())
                    ).length === 0 && (
                      <ListGroup.Item className="text-muted">
                        No stores found
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </div>
              )}
              
              {/* Hidden input to store the selected value */}
              <input type="hidden" value={selectedStore2} />
            </Form.Group>
          </Col>
        </Row>
        
        <div className="text-center mt-3">
          <Button 
            variant="success" 
            size="lg" 
            onClick={handleCompareClick}
            disabled={!selectedStore1 || !selectedStore2 || selectedStore1 === selectedStore2}
          >
            Compare Prices
          </Button>
          {selectedStore1 === selectedStore2 && selectedStore1 !== '' && (
            <div className="text-danger mt-2">
              Please select two different stores to compare
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default StoreSelector;
